import { useMemo } from 'react';

import { CarRateProvider } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { TravelCurrencyCode } from '@graphql/generated-base';
import { parseCarRentalConsolePayload } from '../CarRentalConsole.utils';
import { isTravelCurrencyCode } from '@shared/utils/currency/currencyMapper';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { isCurrencyCodeType } from '@shared/utils/currency/currency.constants';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';

type UseCarRentalCurrencyReturn = Readonly<{
  loading: boolean;
  provider: CarRateProvider;
  currency: TravelCurrencyCode;
  supportedLanguages: ReadonlyArray<string>;
  restrictedCurrencyCodes: ReadonlyArray<CurrencyCodeType> | undefined;
}>;

export const useCarRentalCurrency = (): UseCarRentalCurrencyReturn => {
  const [, providerLoading, , carRentalConsolePayload] = useIsFeatureEnabled('travelCarRentalConsole');
  const { provider, supportedCurrencies, supportedLanguages } = useMemo(() => parseCarRentalConsolePayload(carRentalConsolePayload), [carRentalConsolePayload]);
  const isExpedia = provider === CarRateProvider.EXPEDIA;

  const restrictedCurrencyCodes = useMemo(() => (isExpedia ? supportedCurrencies.filter(isCurrencyCodeType) : undefined), [isExpedia, supportedCurrencies]);
  const restrictedTravelCurrencyCodes = useMemo(() => (isExpedia ? supportedCurrencies.filter(isTravelCurrencyCode) : undefined), [isExpedia, supportedCurrencies]);

  const { currency, loading: currencyLoading } = useTravelWhiteLabelCurrency(undefined, restrictedTravelCurrencyCodes);

  return {
    currency,
    provider,
    supportedLanguages,
    restrictedCurrencyCodes,
    loading: providerLoading || currencyLoading,
  };
};
